import type { CSSProperties } from "react";
import type { CMSSection, LayerWidthStep } from "@/cms/models";
import { getBorderRadius } from "@/utils/theme-helper";
import { boxCss, isCssLength, type BoxSides } from "@/purpose-sections/length";
import {
  resolveBackground,
  type ResolvedBackground,
} from "@/utils/background-layer";

type LayerUi = Partial<CMSSection<unknown>["ui"]>;

export interface LayeredFrame {
  /** the outer <section>: margin and height */
  sectionStyle: CSSProperties;
  /** the section layer: width, padding and where the content layer sits */
  layerClass: string;
  layerStyle: CSSProperties;
  /** the content layer: width, margin, padding, height, corners and shadow */
  contentClass: string;
  contentStyle: CSSProperties;
  section: ResolvedBackground;
  content: ResolvedBackground;
  viewport: boolean;
}

const WIDTH_CLASS: Record<string, string | undefined> = {
  sm: "max-w-screen-sm",
  md: "max-w-screen-md",
  lg: "max-w-screen-lg",
  xl: "max-w-screen-xl",
  "2xl": "max-w-screen-2xl",
  "4xl": "max-w-screen-4xl",
  full: "w-full",
};
const WIDTH_MAX: Record<string, string | undefined> = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  "4xl": "1880px",
  full: "none",
};

/** Bounded, safe inputs only; duplicate breakpoints use the last valid entry. */
export function layerWidthSteps(raw: unknown): LayerWidthStep[] {
  if (!Array.isArray(raw)) return [];
  const steps = new Map<number, string>();
  for (const entry of raw.slice(0, 12)) {
    if (!entry || typeof entry !== "object") continue;
    const { minViewport, width } = entry;
    if (
      !Number.isInteger(minViewport) ||
      minViewport < 0 ||
      minViewport > 3840 ||
      typeof width !== "string"
    )
      continue;
    const value = width.trim();
    if (
      Object.hasOwn(WIDTH_MAX, value) ||
      (isCssLength(value) && Number.parseFloat(value) > 0)
    )
      steps.set(minViewport, value);
  }
  return [...steps]
    .sort(([a], [b]) => a - b)
    .map(([minViewport, width]) => ({ minViewport, width }));
}

/** Selectors and values are validated before any CSS is emitted. */
export function layeredWidthStepsCss(
  ui: LayerUi | undefined,
  scopeClass: string,
): string {
  if (!/^-?[_a-zA-Z][_a-zA-Z0-9-]*$/.test(scopeClass)) return "";
  const section = `.re-layered.${scopeClass} > .re-layer-section`;
  return [
    {
      raw: ui?.widthSteps,
      selector: section,
      variable: "--re-section-step-width",
    },
    {
      raw: ui?.content?.widthSteps,
      selector: `${section} > .re-layer-content`,
      variable: "--re-content-step-width",
    },
  ]
    .flatMap(({ raw, selector, variable }) =>
      layerWidthSteps(raw).map(
        (step) =>
          `@media (min-width: ${step.minViewport}px) { ${selector} { ${variable}: ${WIDTH_MAX[step.width] ?? step.width}; } }`,
      ),
    )
    .join("\n");
}
const ALIGN: Record<string, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};
const VALIGN: Record<string, string> = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};
export const SHADOW: Record<string, string> = {
  none: "none",
  sm: "0 2px 8px #00000012",
  md: "0 6px 18px #00000018",
  lg: "0 12px 30px #00000020",
};
function shorthand(box: BoxSides | undefined): string | undefined {
  return box && `${box.top} ${box.right} ${box.bottom} ${box.left}`;
}

/** Tæthed's top and bottom for a screen a look-system section stores no padding for; the sides keep `md | lg`. */
const DENSITY_PADDING = {
  mobile: "var(--re-space-md, 16px) 16px",
  desktop: "var(--re-space-xl, 32px) 32px",
};

function lengths(raw: unknown): { mobile: string; desktop: string } {
  if (typeof raw !== "string") return { mobile: "auto", desktop: "auto" };
  const parts = raw.split("|").map((part) => part.trim());
  const mobile = isCssLength(parts[0]) ? parts[0] : "auto";
  const desktop =
    parts.length > 1 ? (isCssLength(parts[1]) ? parts[1] : "auto") : mobile;
  return { mobile, desktop };
}

/** `mobile | desktop` maximum heights; absent, `none` or an invalid value is no maximum on that screen. */
function maxLengths(
  raw: unknown,
): { mobile: string; desktop: string } | undefined {
  if (typeof raw !== "string") return undefined;
  const parts = raw.split("|").map((part) => part.trim());
  const mobile = isCssLength(parts[0]) ? parts[0] : "none";
  const desktop =
    parts.length > 1 ? (isCssLength(parts[1]) ? parts[1] : "none") : mobile;
  return mobile === "none" && desktop === "none"
    ? undefined
    : { mobile, desktop };
}

/** A width token becomes its class; any CSS length is an exact max-width. Without either, `followed` (a CSS width the
 *  website sets) stands in for `fallback`. */
function width(
  raw: unknown,
  fallback: string,
  steps?: unknown,
  variable?: string,
  followed?: string,
): { className: string; style: CSSProperties } {
  const token = typeof raw === "string" ? raw.trim() : "";
  if (layerWidthSteps(steps).length) {
    const base = Object.hasOwn(WIDTH_MAX, token)
      ? WIDTH_MAX[token]
      : isCssLength(token)
        ? token
        : (followed ?? WIDTH_MAX[fallback]);
    return {
      className: "w-full",
      style: { maxWidth: `var(${variable}, ${base})` },
    };
  }
  if (token in WIDTH_CLASS)
    return { className: WIDTH_CLASS[token]!, style: {} };
  if (isCssLength(token))
    return { className: "w-full", style: { maxWidth: token } };
  if (followed) return { className: "w-full", style: { maxWidth: followed } };
  return { className: WIDTH_CLASS[fallback]!, style: {} };
}

/** SPEC §1: the section layer defaults to full width, the content layer to 1280 px in the middle. */
export function layeredFrame(
  ui: LayerUi | undefined,
  lookSystem = false,
): LayeredFrame {
  const u = ui ?? {};
  const content = u.content ?? {};
  const sectionWidth = width(
    u.width,
    "full",
    u.widthSteps,
    "--re-section-step-width",
  );
  const contentWidth = width(
    content.width,
    "xl",
    content.widthSteps,
    "--re-content-step-width",
    lookSystem ? `var(--re-content-width, ${WIDTH_MAX.xl})` : undefined,
  );
  const sectionPadding = boxCss(u.padding);
  const sectionMargin = boxCss(u.margin);
  const contentPadding = boxCss(content.padding);
  const contentMargin = boxCss(content.margin);
  const sectionHeight = lengths(u.height);
  const contentHeight = lengths(content.height);
  const sectionMax = maxLengths(u.maxHeight);
  const contentMax = maxLengths(content.maxHeight);
  const contentBackground = resolveBackground(content.background, {
    living: lookSystem,
  });
  const contentStyle: CSSProperties = { ...contentWidth.style };
  const boxFollowsForm = lookSystem && contentBackground.kind !== "none";
  if (content.rounded)
    contentStyle.borderRadius = getBorderRadius(
      content.rounded,
      "card",
      lookSystem,
    );
  else if (boxFollowsForm)
    contentStyle.borderRadius =
      "var(--re-content-box-radius, var(--rounding-card-mobile, 0px))";
  if (content.shadow && content.shadow in SHADOW)
    contentStyle.boxShadow = SHADOW[content.shadow];
  else if (boxFollowsForm) contentStyle.boxShadow = "var(--re-shadow, none)";
  const vars = (
    style: CSSProperties,
    entries: Record<string, string | undefined>,
  ) => {
    for (const [name, value] of Object.entries(entries))
      if (value !== undefined) (style as Record<string, string>)[name] = value;
    return style;
  };
  return {
    sectionStyle: vars(
      {},
      {
        "--section-mobile-height": sectionHeight.mobile,
        "--section-desktop-height": sectionHeight.desktop,
        "--re-section-margin": shorthand(sectionMargin.mobile),
        "--re-section-margin-desktop": shorthand(sectionMargin.desktop),
        "--re-section-margin-x": sectionMargin.mobile
          ? `calc(${sectionMargin.mobile.left} + ${sectionMargin.mobile.right})`
          : undefined,
        "--re-section-margin-x-desktop": sectionMargin.desktop
          ? `calc(${sectionMargin.desktop.left} + ${sectionMargin.desktop.right})`
          : undefined,
      },
    ),
    layerClass: `${sectionWidth.className}${sectionMax ? " re-layer-capped" : ""}`,
    layerStyle: vars(
      { ...sectionWidth.style },
      {
        "--re-section-padding":
          shorthand(sectionPadding.mobile) ??
          (lookSystem ? DENSITY_PADDING.mobile : undefined),
        "--re-section-padding-desktop":
          shorthand(sectionPadding.desktop) ??
          (lookSystem ? DENSITY_PADDING.desktop : undefined),
        "--re-content-x": ALIGN[content.align ?? "center"] ?? "center",
        "--re-content-y": VALIGN[content.valign ?? "center"] ?? "center",
        "--re-section-max-height": sectionMax?.mobile,
        "--re-section-max-height-desktop": sectionMax?.desktop,
      },
    ),
    contentClass: `${contentWidth.className}${contentMax ? " re-layer-capped" : ""}`,
    contentStyle: vars(contentStyle, {
      "--re-content-padding": shorthand(contentPadding.mobile),
      "--re-content-padding-desktop": shorthand(contentPadding.desktop),
      "--re-content-margin": shorthand(contentMargin.mobile),
      "--re-content-margin-desktop": shorthand(contentMargin.desktop),
      "--re-content-margin-x": contentMargin.mobile
        ? `calc(${contentMargin.mobile.left} + ${contentMargin.mobile.right})`
        : undefined,
      "--re-content-margin-x-desktop": contentMargin.desktop
        ? `calc(${contentMargin.desktop.left} + ${contentMargin.desktop.right})`
        : undefined,
      "--re-content-mobile-height": contentHeight.mobile,
      "--re-content-desktop-height": contentHeight.desktop,
      "--re-content-max-height": contentMax?.mobile,
      "--re-content-max-height-desktop": contentMax?.desktop,
    }),
    section: resolveBackground(u.background, { living: lookSystem }),
    content: contentBackground,
    viewport: u.viewport === true,
  };
}
